import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getPoems } from '../services/api';
import './Poems.css';

interface Poem {
  id: number;
  title: string;
  content: string;
  category: string;
}

const Poems: React.FC = () => {
  const [poems, setPoems] = useState<Poem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPoem, setSelectedPoem] = useState<Poem | null>(null);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    getPoems()
      .then((res) => setPoems(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const categories = ['All', ...Array.from(new Set(poems.map((p) => p.category)))];
  const filtered = filter === 'All' ? poems : poems.filter((p) => p.category === filter);

  return (
    <div className="poems-page">
      <section className="poems-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center' }}
          >
            <span className="page-badge">Poetry Collection</span>
            <h1 className="poems-title">
              Words That<br />
              <span className="gradient-text">Move the Soul</span>
            </h1>
            <p className="poems-subtitle">
              Verses born from lived experience, designed to resonate
            </p>
          </motion.div>
        </div>
      </section>

      <section className="poems-content section">
        <div className="container">
          <div className="poems-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`filter-btn ${filter === cat ? 'active' : ''}`}
                onClick={() => setFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <p>No poems available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="poems-grid">
              {filtered.map((poem, index) => (
                <motion.div
                  key={poem.id}
                  className="poem-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedPoem(poem)}
                  whileHover={{ y: -4 }}
                >
                  <div className="poem-card-header">
                    <span className="poem-category">{poem.category}</span>
                    <span className="poem-infinity">&#8734;</span>
                  </div>
                  <h3 className="poem-title">{poem.title}</h3>
                  <div className="poem-preview">
                    {poem.content.split('\n').slice(0, 4).map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                    {poem.content.split('\n').length > 4 && (
                      <span className="poem-more">Read more...</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Poem Modal */}
      <AnimatePresence>
        {selectedPoem && (
          <motion.div
            className="poem-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedPoem(null)}
          >
            <motion.div
              className="poem-modal"
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="poem-modal-close" onClick={() => setSelectedPoem(null)}>
                &times;
              </button>
              <div className="poem-modal-content">
                <span className="poem-category">{selectedPoem.category}</span>
                <h2>{selectedPoem.title}</h2>
                <div className="poem-divider">
                  <span>&#8734;</span>
                </div>
                <div className="poem-full-text">
                  {selectedPoem.content.split('\n').map((line, i) => (
                    <p key={i}>{line || <br />}</p>
                  ))}
                </div>
                <p className="poem-attribution">— Kutloano Mkhulisi</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Poems;
